import React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../Images/logo-reze-xiii_transparent.png';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';

interface DrawerProps {
  readonly window?: () => Window;
}

const drawerWidth = 240;
const navItems = [
  { name: 'ACCUEIL', path: '/' },
  {
    name: 'LE CLUB',
    path: '/presentation',
    subMenu: [
      { name: 'ECOLE DE RUGBY', path: '/ecole-de-rugby' },
      { name: 'FIT XIII', path: '/fit' },
      { name: 'SILVER XIII', path: '/silver' },
      { name: 'TAG XIII', path: '/tag' },
    ],
  },
  { name: 'INSCRIPTIONS', path: '/inscriptions' },
  { name: 'PARTENAIRES', path: '/partenaires' },
  { name: 'CONTACT', path: '/contact' },
];

const DrawerAppBar: React.FC<DrawerProps> = (props: DrawerProps) => {
  const { window } = props;
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(prevState => !prevState);
  };

  const handleOpenSubMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSubMenu = () => {
    setAnchorEl(null);
  };

  const handleSubMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const isActive = (path: string) => location.pathname === path;

  const drawer = (
    <Box sx={{ textAlign: 'center' }}>
      <IconButton
        onClick={handleDrawerToggle}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.secondary.main,
          '&:hover': {
            color: theme.palette.primary.main,
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Typography
        variant="h6"
        component="div"
        sx={{
          my: 3,
          color: theme.palette.secondary.main,
          fontWeight: 700,
        }}
      >
        Rezé XIII
      </Typography>
      <Divider sx={{ backgroundColor: theme.palette.primary.main }} />
      <List>
        {navItems.map(item => (
          <div key={item.name}>
            {item.subMenu ? (
              <Accordion
                sx={{
                  backgroundColor: isActive(item.path)
                    ? theme.palette.primary.light
                    : theme.palette.primary.main,
                  boxShadow: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                  },
                }}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#111111' }} />}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: '#111111',
                    }}
                  >
                    {item.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List component="div" disablePadding>
                    {item.subMenu.map(subItem => (
                      <ListItem key={subItem.name} onClick={handleSubMenuClick}>
                        <ListItemButton
                          component={Link}
                          to={subItem.path}
                          sx={{
                            backgroundColor: isActive(subItem.path)
                              ? theme.palette.secondary.main
                              : 'transparent',
                            color: isActive(subItem.path)
                              ? theme.palette.primary.main
                              : theme.palette.secondary.main,
                            fontWeight: isActive(subItem.path) ? 600 : 400,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: theme.palette.secondary.light,
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          <ListItemText
                            primary={subItem.name}
                            primaryTypographyProps={{
                              sx: {
                                color: isActive(subItem.path)
                                  ? theme.palette.primary.main
                                  : '#111111',
                                fontWeight: isActive(subItem.path) ? 700 : 600,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ) : (
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  backgroundColor: isActive(item.path)
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main,
                  color: isActive(item.path)
                    ? theme.palette.primary.main
                    : theme.palette.secondary.main,
                  fontWeight: isActive(item.path) ? 600 : 400,
                  transition: 'all 0.3s ease',
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    sx: {
                      color: isActive(item.path)
                        ? theme.palette.primary.main
                        : '#111111',
                      fontWeight: isActive(item.path) ? 700 : 600,
                    },
                  }}
                />
              </ListItemButton>
            )}
          </div>
        ))}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : null;

  return (
    <Box sx={{ display: 'flex', marginBottom: '3rem' }}>
      <CssBaseline />
      <AppBar
        component="nav"
        sx={{
          backgroundColor: theme.palette.secondary.main,
          borderBottom: `3px solid ${theme.palette.primary.main}`,
        }}
      >
        <Toolbar
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.secondary.main,
            py: 1,
          }}
        >
          <Box
            sx={{
              display: { xs: 'flex', sm: 'flex' },
              alignItems: 'center',
              justifyContent: { xs: 'start', sm: 'start' },
              flexGrow: 1,
            }}
          >
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textDecoration: 'none',
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Logo"
                loading="lazy"
                sx={{
                  height: 60,
                  width: 'auto',
                  borderRadius: '8px',
                  transition: 'transform 0.3s ease, filter 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    filter: 'brightness(1.1)',
                  },
                }}
              />
            </Link>
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {navItems.map(item =>
              item.subMenu ? (
                <React.Fragment key={item.name}>
                  <Button
                    aria-controls="simple-menu"
                    aria-haspopup="true"
                    onClick={handleOpenSubMenu}
                    sx={{
                      color: '#111111',
                      margin: '0 0.5rem',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      textTransform: 'none',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: '#000000',
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        borderRadius: '4px',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -6,
                        left: 0,
                        width: 0,
                        height: '3px',
                        backgroundColor: '#111111',
                        transition: 'width 0.3s ease',
                      },
                      '&:hover::after': {
                        width: '100%',
                      },
                    }}
                  >
                    {item.name}
                  </Button>
                  <Menu
                    id="simple-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseSubMenu}
                    PaperProps={{
                      sx: {
                        backgroundColor: theme.palette.secondary.light,
                        border: `2px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    {item.subMenu.map(subItem => (
                      <MenuItem
                        key={subItem.name}
                        onClick={handleCloseSubMenu}
                        component={Link}
                        to={subItem.path}
                        sx={{
                          backgroundColor: isActive(subItem.path)
                            ? theme.palette.primary.main
                            : 'transparent',
                          color: isActive(subItem.path)
                            ? theme.palette.secondary.main
                            : theme.palette.text.primary,
                          fontWeight: isActive(subItem.path) ? 600 : 400,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.secondary.main,
                          },
                        }}
                      >
                        {subItem.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </React.Fragment>
              ) : (
                <Button
                  key={item.name}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: isActive(item.path) ? '#000000' : '#111111',
                    margin: '0 0.5rem',
                    fontWeight: isActive(item.path) ? 800 : 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    backgroundColor: isActive(item.path)
                      ? 'rgba(0, 0, 0, 0.15)'
                      : 'transparent',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    borderRadius: '4px',
                    px: 1.5,
                    '&:hover': {
                      color: '#000000',
                      backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -6,
                      left: 0,
                      width: isActive(item.path) ? '100%' : 0,
                      height: '3px',
                      backgroundColor: '#111111',
                      transition: 'width 0.3s ease',
                    },
                    '&:hover::after': {
                      width: '100%',
                    },
                  }}
                >
                  {item.name}
                </Button>
              ),
            )}
          </Box>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="end"
            onClick={handleDrawerToggle}
            sx={{
              mr: 0,
              display: { sm: 'none' },
              color: '#111111',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <MenuIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.secondary.main,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
};

export default DrawerAppBar;
